import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { EmailVerification } from '../components/EmailVerification';

export const Login: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const { signIn, signUp } = useAuth();
  const { theme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isLogin) {
        await signIn(email, password);
      } else {
        await signUp(email, password);
        setSignupEmail(email);
        setShowEmailVerification(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification screen after signup
  if (showEmailVerification) {
    return (
      <EmailVerification
        email={signupEmail}
        onBack={() => {
          setShowEmailVerification(false);
          setSignupEmail('');
          setEmail('');
          setPassword('');
          setIsLogin(true);
        }}
      />
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundColor: theme === 'dark' ? '#111827' : '#f0f4f8'
      }}
    >
      <div 
        className="p-6 sm:p-8 rounded-lg shadow-lg border-2 w-full max-w-md"
        style={{
          backgroundColor: theme === 'dark' ? 'rgb(30 41 59)' : '#ffffff',
          borderColor: theme === 'dark' ? '#ffffff' : '#111827'
        }}
      >
        <h2 
          className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center"
          style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
        >
          {isLogin ? 'Sign In' : 'Sign Up'}
        </h2>
        
        {error && (
          <div 
            className="border px-4 py-3 rounded-lg mb-4 text-sm"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              borderColor: '#ef4444',
              color: theme === 'dark' ? '#fca5a5' : '#dc2626'
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label 
              htmlFor="email" 
              className="block text-sm font-medium mb-1"
              style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent"
              style={{
                borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
                backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                color: theme === 'dark' ? '#e5e7eb' : '#111827'
              }}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label 
              htmlFor="password" 
              className="block text-sm font-medium mb-1"
              style={{ color: theme === 'dark' ? '#e5e7eb' : '#374151' }}
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 pr-10 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:border-transparent"
                style={{
                  borderColor: theme === 'dark' ? '#4b5563' : '#d1d5db',
                  backgroundColor: theme === 'dark' ? '#111827' : '#ffffff',
                  color: theme === 'dark' ? '#e5e7eb' : '#111827'
                }}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{
                  color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            style={{
              backgroundColor: theme === 'dark' ? '#ffffff' : '#111827',
              color: theme === 'dark' ? '#111827' : '#ffffff'
            }}
          >
            {loading ? 'Loading...' : isLogin ? 'Sign In' : 'Sign Up'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
            }}
            className="hover:underline text-sm"
            style={{
              color: theme === 'dark' ? '#ffffff' : '#111827'
            }}
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
};
